import {
  Pagination,
  ResponsiveTable,
  ResponsiveTableContext,
  TResponsiveTableColumn,
  TResponsiveTableSortChangeEvent,
  TResponsiveTableSortValue,
} from '@apia/table';
import { TApiaFilter, TApiaFormElement, TModify, getIndex } from '@apia/util';
import { useFetcher } from '../hooks/defaultFetcher';
import { useCallback, useEffect, useState } from 'react';
import { TModalData } from '../hooks/types';
import { Box } from '@apia/theme';
import { ApiaPoolsModalEmitter } from '../../../fields/ApiaApiPoolsModalField';
import { getModal } from '../../../apiaApi';

export const ApiaPoolsModal = (props: {
  tableName?: string;
  initialProps: TModify<
    TApiaFormElement,
    {
      onChange: React.ChangeEventHandler<Element>;
    }
  >;
}) => {
  const [actualColumns, setColumns] = useState<TResponsiveTableColumn[]>(
    getModal('poolsModal').columns.map((current) => ({
      ...current,
      currentSorting: getIndex<TResponsiveTableSortValue>(
        ['A', 'D', null],
        [
          ['A', 'Up'].includes(current.currentSorting || ''),
          ['D', 'Down'].includes(current.currentSorting || ''),
          true,
        ],
      ),
    })),
  );
  const [filters, setFilters] = useState(getModal('poolsModal').filters);
  const [state, setState] = useState<TModalData | null>(null);
  const { fetcher } = useFetcher();
  const filter = useCallback(
    (newFilters: TApiaFilter[] = filters) => {
      if (newFilters) {
        fetcher
          .filter({
            filters: newFilters,
            parameters: {
              txtName: props.initialProps.name,
              txtTitle: props.initialProps.title,
              txtDesc: props.initialProps.title,
              selectOnlyOne: false,
              showAutogenerated: false,
              showNotAutogenerated: false,
              showCurrentEnv: false,
              showGlobal: false,
              fromEnvs: '',
              exactMatch: '',
              showOnlyPoolsInHierarchy: false,
              forHierarchy: false,
              showOnlyPoolsOutsideHierarchy: false,
              refreshParameters: false,
              fetchAllInHierarchy: false,
              usrToUpdate: '',
            },
            path: getModal('poolsModal').path,
            requestConfig: {},
          })
          .then((result) => {
            if (result) {
              setState(result);
            }
          })
          .catch(console.error);
      }
    },
    [fetcher, filters, props.initialProps.name, props.initialProps.title],
  );
  useEffect(() => {
    filter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    ApiaPoolsModalEmitter.on('deleteFilters', filter);
  }, [filter]);

  const handleFilterChange = useCallback(
    (ev: TApiaFilter) => {
      const newFilters = filters?.map((currentFilter) => {
        if (currentFilter.column === ev.column) {
          return { ...ev };
        }
        return currentFilter;
      });
      setFilters(newFilters);
      if (ev.runAutomatically) filter(newFilters);
    },
    [filters, filter],
  );
  const handleSort = useCallback(
    (ev: TResponsiveTableSortChangeEvent) => {
      fetcher
        .sort({
          sort: ev.columnIndex,
          parameters: {},
          path: getModal('poolsModal').path,
          requestConfig: {},
        })
        .then((result) => {
          if (result) {
            setColumns((currentColumns) =>
              currentColumns.map((column) => {
                if (column.name === ev.name)
                  return {
                    ...column,
                    currentSorting:
                      column.currentSorting === 'Down' ||
                      column.currentSorting === 'D' ||
                      !column.currentSorting
                        ? 'A'
                        : 'D',
                  };
                return { ...column, currentSorting: null };
              }),
            );
            setState(result);
          }
        })
        .catch(console.error);
    },
    [fetcher],
  );
  const handleFilterPressEnter = useCallback(() => filter(), [filter]);
  const handleFilterBlur = useCallback(() => filter(), [filter]);

  const handlePageChange = useCallback(
    (page: number) => {
      fetcher
        .page({
          page,
          parameters: {},
          path: getModal('poolsModal').path,
        })
        .then((result) => {
          if (result) {
            setState(result);
          }
        })
        .catch(console.error);
    },
    [fetcher],
  );
  const handleRefresh = useCallback(() => {
    fetcher
      .refresh({
        parameters: {},
        path: getModal('poolsModal').path,
      })
      .then((result) => {
        if (result) {
          setState(result);
        }
      })
      .catch(console.error);
  }, [fetcher]);

  return (
    state?.rows && (
      <ResponsiveTableContext
        columns={actualColumns}
        rows={state?.rows}
        filters={filters}
        name={props.tableName ?? getModal('poolsModal').label}
        variant={'layout.common.modals.table'}
        onFilterChange={handleFilterChange}
        onSortChange={handleSort}
        onFilterBlur={handleFilterBlur}
        onFilterPressEnter={handleFilterPressEnter}
        isMultiple
      >
        <ResponsiveTable />
        <Box className="pagination__wrapper">
          <Pagination
            currentPage={state.currentPage}
            onPageChange={handlePageChange}
            onRefresh={handleRefresh}
            pageCount={state.pageCount}
            reachedMax={state.reachedMax}
            recordsCount={state.recordsCount}
          />
        </Box>
      </ResponsiveTableContext>
    )
  );
};
